import { toLocalISOString } from '@/lib'
import { DateField, DateRangeField } from '@/ui'
import { campaignsShowQueryKey, useCampaignScheduleUpdate } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { addDays, endOfDay, format, startOfDay } from 'date-fns'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MAX_DURATION_DAYS = 28

export interface EditScheduleSheetProps {
	onClose: () => void
	campaignId: string
	startDate: Date
	endDate: Date
}

export function EditScheduleSheet({ onClose, campaignId, startDate, endDate }: EditScheduleSheetProps) {
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()

	const [start, setStart] = useState<Date>(startDate)
	const [end, setEnd] = useState<Date>(endDate)

	const isDelivering = startOfDay(startDate) <= startOfDay(new Date())

	const mutation = useCampaignScheduleUpdate({
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: campaignsShowQueryKey(campaignId) })
			onClose()
		},
	})

	const close = useCallback(() => {
		mutation.reset()
		onClose()
	}, [mutation, onClose])

	const handleSave = useCallback(() => {
		mutation.mutate({
			campaign: campaignId,
			data: {
				...(isDelivering ? {} : { start_date: toLocalISOString(start) }),
				// DateField (delivering path) emits 00:00; normalise so the campaign
				// runs through the whole end day instead of ending at its start.
				endDate: toLocalISOString(endOfDay(end)),
			},
		})
	}, [campaignId, end, isDelivering, mutation, start])

	const minDate = startOfDay(new Date())

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			<div className="absolute inset-0 bg-black/50" onClick={close} />

			<div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 flex flex-col">
				<div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
					<h2 className="text-sm font-medium text-grey-900">{t('overview.schedule.title')}</h2>
					<button
						type="button"
						onClick={close}
						aria-label={t('overview.schedule.cancel')}
						className="text-grey-500 hover:text-grey-900"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 20 20"
							fill="currentColor"
							className="size-4"
						>
							<path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
						</svg>
					</button>
				</div>

				<div className="p-5 space-y-4">
					<p className="text-sm text-grey-600">{t('overview.schedule.description')}</p>

					{isDelivering
						? (
							<div className="space-y-4">
								<div>
									<div className="text-sm font-medium text-grey-700">
										{t('overview.schedule.startLabel')}
									</div>
									<div className="text-base text-grey-900">{format(start, 'dd MMM yyyy')}</div>
									<p className="mt-1 text-xs text-grey-500">
										{t('overview.schedule.startLockedHint')}
									</p>
								</div>
								<DateField
									label={t('overview.schedule.endLabel')}
									value={end}
									onChange={(value) => {
										setEnd(value)
										mutation.reset()
									}}
									minDate={minDate}
									maxDate={addDays(startOfDay(start), MAX_DURATION_DAYS - 1)}
								/>
							</div>
						)
						: (
							<DateRangeField
								label={t('overview.schedule.dateRangeLabel')}
								value={[start, end]}
								onChange={(dates) => {
									setStart(dates[0])
									setEnd(dates[1])
									mutation.reset()
								}}
								minDate={minDate}
								maxDurationDays={MAX_DURATION_DAYS}
							/>
						)}

					{mutation.isError && <p className="text-sm text-red-600">{t('overview.schedule.saveError')}</p>}
				</div>

				<div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200">
					<button
						type="button"
						onClick={close}
						className="px-3 py-2 text-sm text-grey-700 hover:text-grey-900"
					>
						{t('overview.schedule.cancel')}
					</button>
					<button
						type="button"
						onClick={handleSave}
						disabled={mutation.isPending}
						className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
					>
						{mutation.isPending ? t('overview.schedule.saving') : t('overview.schedule.save')}
					</button>
				</div>
			</div>
		</div>
	)
}
